// Виджет чата с поддержкой (Dynamics 365 Omnichannel for Customer Service).
//
// Оформление и логика интерфейса — свои, а канал связи — официальный
// Omnichannel Chat SDK: он ходит в тот же Live Chat Widget (app-id), что и
// стандартный виджет Microsoft, и разговор попадает к агентам в Omnichannel
// как обычно. SDK тяжёлый (~1,4 МБ), поэтому лежит отдельным файлом
// chat.sdk.min.js и грузится только когда посетитель нажал «Начать чат» или
// у него уже есть незавершённый разговор.
//
// Разметка (кнопка и панель) приходит с сервера из Pages/Shared/_ChatWidget.cshtml
// вместе с настройками в data-* корня #zs-chat и локализованными строками в
// data-labels. Цвета берутся из токенов темы, так что светлая и тёмная темы
// переключаются без участия скрипта.
//
// Разговор переживает переходы между страницами: контекст чата лежит в
// sessionStorage, при загрузке страницы SDK переподключается к нему и
// подтягивает историю сообщений.

(function () {
    "use strict";

    const root = document.getElementById("zs-chat");
    if (!root) {
        return;
    }

    // --- Контракты -----------------------------------------------------------

    /** Сообщение SDK: нужна только малая часть полей. */
    interface SdkMessage {
        id?: string;
        messageid?: string;
        clientmessageid?: string;
        content?: string;
        contentType?: string;
        messageType?: string;
        role?: string;
        tags?: string[] | string;
        metadata?: { tags?: string | string[] };
        properties?: { tags?: string | string[] };
        sender?: { displayName?: string; id?: string };
        timestamp?: Date | string;
        fileMetadata?: { id?: string; name?: string; type?: string; size?: number; url?: string };
    }

    interface ChatSdk {
        initialize(): Promise<{ LiveWSAndLiveChatEngJoin?: { OutOfOperatingHours?: string } } | undefined>;
        getAgentAvailability(): Promise<{
            isQueueAvailable?: boolean;
            StartTimeOfNextOperatingHour?: string;
            EndTimeOfNextOperatingHour?: string;
        }>;
        startChat(params?: Record<string, unknown>): Promise<void>;
        endChat(): Promise<void>;
        getConversationDetails(): Promise<{ state?: string } | undefined>;
        getCurrentLiveChatContext(): Promise<Record<string, unknown>>;
        getMessages(): Promise<SdkMessage[] | undefined>;
        sendMessage(message: { content: string }): Promise<unknown>;
        sendTypingEvent(): Promise<void>;
        onNewMessage(callback: (message: SdkMessage) => void): Promise<void>;
        onTypingEvent(callback: () => void): Promise<void>;
        onAgentEndSession(callback: () => void): Promise<void>;
        uploadFileAttachment(file: File): Promise<unknown>;
        downloadFileAttachment(meta: object): Promise<Blob>;
    }

    interface ChatSdkModule {
        OmnichannelChatSDK: new (
            omnichannel: { orgId: string; orgUrl: string; widgetId: string },
            config?: Record<string, unknown>
        ) => ChatSdk;
    }

    type Phase = "idle" | "connecting" | "active" | "ended" | "error";

    // --- Настройки и строки --------------------------------------------------

    const data = root.dataset;
    const labels: Record<string, string> = (() => {
        try {
            return JSON.parse(data.labels ?? "{}");
        } catch {
            return {};
        }
    })();
    const t = (key: string): string => labels[key] ?? "";

    const STORAGE_KEY = "zs-chat";
    const TYPING_VISIBLE_MS = 6000;
    const TYPING_SEND_INTERVAL_MS = 3000;
    const MAX_FILE_BYTES = 20 * 1024 * 1024;

    /** displayName клиента в сообщениях ACS (см. isCustomerMessage в SDK). */
    const CUSTOMER_DISPLAY_NAME = "__customer__";

    // --- Элементы ------------------------------------------------------------

    function el<T extends HTMLElement>(name: string): T {
        return root!.querySelector<T>(`[data-chat="${name}"]`)!;
    }

    const launcher = el<HTMLButtonElement>("launcher");
    const panel = el<HTMLElement>("panel");
    const status = el<HTMLElement>("status");
    const welcome = el<HTMLElement>("welcome");
    const welcomeText = el<HTMLElement>("welcome-text");
    const startButton = el<HTMLButtonElement>("start");
    const requestLink = el<HTMLAnchorElement>("request");
    const log = el<HTMLElement>("log");
    const typing = el<HTMLElement>("typing");
    const notice = el<HTMLElement>("notice");
    const form = el<HTMLFormElement>("form");
    const input = el<HTMLTextAreaElement>("input");
    const sendButton = el<HTMLButtonElement>("send");
    const attachButton = el<HTMLButtonElement>("attach");
    const fileInput = el<HTMLInputElement>("file");
    const endButton = el<HTMLButtonElement>("end");
    const minimizeButton = el<HTMLButtonElement>("minimize");

    // --- Состояние -----------------------------------------------------------

    let phase: Phase = "idle";
    let sdk: ChatSdk | null = null;
    let sdkLoading: Promise<ChatSdk> | null = null;
    let initialized = false;
    let probing = false;
    /** null — ещё не узнали; true — вне рабочих часов, начать чат нельзя. */
    let offline: boolean | null = null;
    let lastSender: string | null = null;
    let typingTimer = 0;
    let lastTypingSent = 0;
    const seen = new Set<string>();
    /** Тексты, показанные сразу при отправке, — эхо из SDK их повторять не должно. */
    const pendingEcho: string[] = [];

    interface Saved {
        context: Record<string, unknown>;
        open: boolean;
    }

    function readSaved(): Saved | null {
        try {
            const raw = sessionStorage.getItem(STORAGE_KEY);
            return raw ? (JSON.parse(raw) as Saved) : null;
        } catch {
            return null;
        }
    }

    function writeSaved(value: Saved | null): void {
        try {
            if (value) {
                sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
            } else {
                sessionStorage.removeItem(STORAGE_KEY);
            }
        } catch {
            /* sessionStorage недоступен — чат работает, но после перехода
               на другую страницу разговор придётся начинать заново. */
        }
    }

    function remember(): void {
        const saved = readSaved();
        if (saved) {
            saved.open = isOpen();
            writeSaved(saved);
        }
    }

    // --- Подключение SDK -----------------------------------------------------

    async function getAuthToken(): Promise<string | null> {
        try {
            const response = await fetch("/_service/auth/token", { credentials: "same-origin", cache: "no-store" });
            return response.ok ? (await response.text()).trim() : null;
        } catch {
            return null;
        }
    }

    function loadSdk(): Promise<ChatSdk> {
        if (sdkLoading) {
            return sdkLoading;
        }

        sdkLoading = new Promise<ChatSdk>((resolve, reject) => {
            const create = (): void => {
                const module = (window as unknown as { zsChatSdk?: ChatSdkModule }).zsChatSdk;
                if (!module) {
                    reject(new Error("Chat SDK is not available"));
                    return;
                }

                resolve(new module.OmnichannelChatSDK(
                    {
                        orgId: data.orgId ?? "",
                        orgUrl: data.orgUrl ?? "",
                        widgetId: data.appId ?? ""
                    },
                    {
                        // Своего ключа телеметрии Microsoft у нас нет.
                        telemetry: { disable: true },
                        chatReconnect: { disable: true },
                        // Вошедший клиент: токен подписывает портал (ServiceController),
                        // по нему Dynamics находит клиента вместо Visitor.
                        ...(data.auth === "1" ? { getAuthToken } : {})
                    }));
            };

            if ((window as unknown as { zsChatSdk?: unknown }).zsChatSdk) {
                create();
                return;
            }

            const script = document.createElement("script");
            script.src = data.sdk ?? "";
            script.async = true;
            script.onload = create;
            script.onerror = () => reject(new Error("Chat SDK failed to load"));
            document.head.appendChild(script);
        }).catch((error) => {
            sdkLoading = null;
            throw error;
        });

        return sdkLoading;
    }

    // --- Интерфейс -----------------------------------------------------------

    function isOpen(): boolean {
        return !panel.hidden;
    }

    function setOpen(open: boolean, focus = true): void {
        panel.hidden = !open;
        launcher.setAttribute("aria-expanded", String(open));
        root!.classList.toggle("is-open", open);
        remember();

        if (open && phase === "idle") {
            void probe();
        }

        if (open && focus) {
            (phase === "active" ? input : welcome.hidden ? minimizeButton : startButton).focus();
            scrollToEnd();
        } else if (!open && focus) {
            launcher.focus();
        }
    }

    function setStatus(text: string): void {
        status.textContent = text;
    }

    function showNotice(text: string, actions: { label: string; run: () => void; primary?: boolean }[] = []): void {
        notice.replaceChildren();
        if (!text) {
            notice.hidden = true;
            return;
        }

        notice.appendChild(Object.assign(document.createElement("span"), { textContent: text }));
        for (const action of actions) {
            const button = Object.assign(document.createElement("button"), {
                type: "button",
                textContent: action.label,
                className: "zs-chat-btn" + (action.primary ? " is-primary" : "")
            });
            button.addEventListener("click", action.run);
            notice.appendChild(button);
        }
        notice.hidden = false;
    }

    function setPhase(next: Phase): void {
        phase = next;
        root!.dataset.phase = next;

        const live = next === "active";
        welcome.hidden = next !== "idle" && next !== "error";
        log.hidden = !welcome.hidden;
        form.hidden = !live;
        endButton.hidden = !(live || next === "connecting");
        startButton.disabled = next === "connecting";
        input.disabled = !live;
        sendButton.disabled = !live;
        attachButton.disabled = !live;

        if (next === "idle") {
            setStatus("");
        } else if (next === "connecting") {
            setStatus(t("connecting"));
        } else if (next === "ended") {
            setStatus(t("ended"));
        }
    }

    function scrollToEnd(): void {
        log.scrollTop = log.scrollHeight;
    }

    function formatTime(value: Date | string | undefined): string {
        const date = value ? new Date(value) : new Date();
        return Number.isNaN(date.getTime())
            ? ""
            : date.toLocaleTimeString(document.documentElement.lang || undefined, { hour: "2-digit", minute: "2-digit" });
    }

    // Сообщения агента могут прийти как RichText. Оставляем безопасный минимум
    // разметки; всё остальное превращаем в текст, ссылки открываем в новой вкладке.
    const ALLOWED_TAGS = new Set(["A", "B", "STRONG", "I", "EM", "U", "BR", "P", "UL", "OL", "LI", "CODE", "PRE"]);
    /** Теги, чей текст показывать не нужно: содержимое отбрасывается целиком. */
    const DROPPED_TAGS = new Set(["SCRIPT", "STYLE", "IFRAME", "OBJECT", "EMBED", "TEMPLATE", "NOSCRIPT", "SVG", "MATH"]);

    function sanitize(html: string): DocumentFragment {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const fragment = document.createDocumentFragment();

        const copy = (source: Node, target: Node): void => {
            source.childNodes.forEach((node) => {
                if (node.nodeType === Node.TEXT_NODE) {
                    target.appendChild(document.createTextNode(node.textContent ?? ""));
                    return;
                }
                if (node.nodeType !== Node.ELEMENT_NODE) {
                    return;
                }

                const source = node as HTMLElement;
                if (DROPPED_TAGS.has(source.tagName.toUpperCase())) {
                    return;
                }
                if (!ALLOWED_TAGS.has(source.tagName)) {
                    copy(source, target);
                    return;
                }

                const clean = document.createElement(source.tagName);
                if (source.tagName === "A") {
                    const href = source.getAttribute("href") ?? "";
                    if (/^(https?:|mailto:)/i.test(href)) {
                        clean.setAttribute("href", href);
                        clean.setAttribute("target", "_blank");
                        clean.setAttribute("rel", "noopener noreferrer");
                    }
                }
                copy(source, clean);
                target.appendChild(clean);
            });
        };

        copy(doc.body, fragment);
        return fragment;
    }

    const EMAIL = /[^\s<>()"']+@[^\s<>()"']+\.[^\s<>()"']+/;

    // Omnichannel может отдавать в имени агента его e-mail (UPN) или «Имя <e-mail>».
    // Адрес наружу не показываем: вырезаем его из имени, а если имя состояло
    // только из адреса — собираем имя из локальной части («anna.k@…» -> «Anna K»).
    function agentName(raw: string | undefined): string {
        const value = (raw ?? "").trim();
        if (!value) {
            return "";
        }

        const stripped = value.replace(new RegExp(EMAIL.source, "g"), "").replace(/[<>()]/g, "").replace(/\s+/g, " ").trim();
        if (stripped) {
            return stripped;
        }

        const local = EMAIL.exec(value)?.[0].split("@")[0] ?? "";
        return local
            .split(/[._\-+]+/)
            .filter(Boolean)
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join(" ");
    }

    // Боты (QnA, Copilot Studio) отвечают в Markdown. Разбираем минимальное
    // подмножество и строим DOM напрямую, без innerHTML: чужая разметка в
    // страницу не попадает, ссылки пропускаются только http(s) и mailto.
    const INLINE = /\*\*([^*]+?)\*\*|\*([^*\s][^*]*?)\*|`([^`]+)`|\[([^\]]+)\]\(((?:https?:|mailto:)[^\s)]+)\)|(https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"])|\[([^\]\s]+)\](?![(:])/g;

    /** Определения сносок вида «[1]: https://… "Заголовок"» (reference-style ссылки Markdown). */
    interface MarkdownRef {
        url: string;
        title: string;
    }

    const REF_DEFINITION = /^\s*\[([^\]\s]+)\]:\s*(https?:\/\/\S+)(?:\s+["'\u201c](.*?)["'\u201d])?\s*$/;

    function appendInline(target: Node, text: string, refs: Map<string, MarkdownRef>): void {
        let last = 0;
        for (const match of text.matchAll(INLINE)) {
            const index = match.index ?? 0;
            if (index > last) {
                target.appendChild(document.createTextNode(text.slice(last, index)));
            }
            last = index + match[0].length;

            const [, bold, italic, code, label, href, bare, cite] = match;
            if (bold !== undefined) {
                const node = document.createElement("strong");
                appendInline(node, bold, refs);
                target.appendChild(node);
            } else if (italic !== undefined) {
                const node = document.createElement("em");
                appendInline(node, italic, refs);
                target.appendChild(node);
            } else if (code !== undefined) {
                target.appendChild(Object.assign(document.createElement("code"), { textContent: code }));
            } else if (cite !== undefined) {
                // Сноска [1]: ссылка на источник, если он определён ниже в тексте.
                const ref = refs.get(cite);
                if (ref) {
                    target.appendChild(Object.assign(document.createElement("a"), {
                        href: ref.url,
                        target: "_blank",
                        rel: "noopener noreferrer",
                        title: ref.title,
                        textContent: `[${cite}]`
                    }));
                } else {
                    target.appendChild(document.createTextNode(match[0]));
                }
            } else {
                const url = href ?? bare ?? "";
                const link = Object.assign(document.createElement("a"), {
                    href: url,
                    target: "_blank",
                    rel: "noopener noreferrer",
                    // Без разбора внутри ссылки: адрес в подписи иначе снова станет ссылкой.
                    textContent: label ?? url
                });
                target.appendChild(link);
            }
        }
        if (last < text.length) {
            target.appendChild(document.createTextNode(text.slice(last)));
        }
    }

    function renderMarkdown(source: string): DocumentFragment {
        const fragment = document.createDocumentFragment();
        const lines = source.replace(/\r\n?/g, "\n").split("\n");

        const refs = new Map<string, MarkdownRef>();
        for (const line of lines) {
            const definition = REF_DEFINITION.exec(line);
            if (definition) {
                refs.set(definition[1], { url: definition[2], title: definition[3] ?? "" });
            }
        }

        let paragraph: HTMLElement | null = null;
        let list: HTMLElement | null = null;
        let listKind = "";

        const flush = (): void => {
            paragraph = null;
            list = null;
            listKind = "";
        };

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];

            if (/^\s*```/.test(line)) {
                const code: string[] = [];
                for (i++; i < lines.length && !/^\s*```/.test(lines[i]); i++) {
                    code.push(lines[i]);
                }
                const pre = document.createElement("pre");
                pre.appendChild(Object.assign(document.createElement("code"), { textContent: code.join("\n") }));
                fragment.appendChild(pre);
                flush();
                continue;
            }

            if (!line.trim()) {
                flush();
                continue;
            }

            // «[1]: адрес "Заголовок"» показываем одной ссылкой: номер и заголовок страницы
            // вместе, а не адрес отдельной строкой.
            const definition = REF_DEFINITION.exec(line);
            if (definition) {
                const ref = refs.get(definition[1])!;
                const row = Object.assign(document.createElement("div"), { className: "zs-chat-ref" });
                row.appendChild(Object.assign(document.createElement("a"), {
                    href: ref.url,
                    target: "_blank",
                    rel: "noopener noreferrer",
                    textContent: `[${definition[1]}] ${ref.title || ref.url}`
                }));
                fragment.appendChild(row);
                flush();
                continue;
            }

            const heading = /^\s{0,3}#{1,6}\s+(.*?)\s*#*\s*$/.exec(line);
            if (heading) {
                const node = Object.assign(document.createElement("div"), { className: "zs-chat-heading-md" });
                appendInline(node, heading[1], refs);
                fragment.appendChild(node);
                flush();
                continue;
            }

            const item = /^\s*(?:([-*+])|(\d+)[.)])\s+(.*)$/.exec(line);
            if (item) {
                const kind = item[1] ? "ul" : "ol";
                if (!list || listKind !== kind) {
                    list = document.createElement(kind);
                    listKind = kind;
                    fragment.appendChild(list);
                }
                const li = document.createElement("li");
                appendInline(li, item[3], refs);
                list.appendChild(li);
                paragraph = null;
                continue;
            }

            list = null;
            listKind = "";
            if (!paragraph) {
                paragraph = document.createElement("p");
                fragment.appendChild(paragraph);
            } else {
                paragraph.appendChild(document.createElement("br"));
            }
            appendInline(paragraph, line.trim(), refs);
        }

        return fragment;
    }

    interface Bubble {
        kind: "user" | "agent" | "system";
        text?: string;
        html?: string;
        markdown?: string;
        sender?: string;
        time?: Date | string;
        file?: { name: string; size?: number; meta?: object };
    }

    function addBubble(bubble: Bubble): HTMLElement {
        const item = document.createElement("div");
        item.className = `zs-chat-msg is-${bubble.kind}`;

        if (bubble.kind === "system") {
            item.textContent = bubble.text ?? "";
            lastSender = null;
        } else {
            const who = bubble.kind === "agent" ? (bubble.sender || t("agent")) : "";
            if (bubble.kind === "agent" && who !== lastSender) {
                item.appendChild(Object.assign(document.createElement("div"), {
                    className: "zs-chat-sender",
                    textContent: who
                }));
            }
            lastSender = bubble.kind === "agent" ? who : "user";

            const body = document.createElement("div");
            body.className = "zs-chat-bubble";

            if (bubble.file) {
                body.appendChild(fileChip(bubble.file));
            } else if (bubble.html) {
                body.appendChild(sanitize(bubble.html));
            } else if (bubble.markdown) {
                body.classList.add("is-md");
                body.appendChild(renderMarkdown(bubble.markdown));
            } else {
                body.textContent = bubble.text ?? "";
            }

            item.appendChild(body);
            item.appendChild(Object.assign(document.createElement("time"), {
                className: "zs-chat-time",
                textContent: formatTime(bubble.time)
            }));
        }

        log.appendChild(item);
        scrollToEnd();
        return item;
    }

    function fileChip(file: { name: string; meta?: object }): HTMLElement {
        const chip = document.createElement(file.meta ? "button" : "span");
        chip.className = "zs-chat-file";
        chip.textContent = file.name;

        if (file.meta) {
            (chip as HTMLButtonElement).type = "button";
            chip.addEventListener("click", async () => {
                try {
                    const blob = await sdk!.downloadFileAttachment(file.meta!);
                    const url = URL.createObjectURL(blob);
                    const link = Object.assign(document.createElement("a"), { href: url, download: file.name });
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    setTimeout(() => URL.revokeObjectURL(url), 10000);
                } catch {
                    showNotice(t("error"));
                }
            });
        }

        return chip;
    }

    function showTyping(): void {
        typing.hidden = false;
        window.clearTimeout(typingTimer);
        typingTimer = window.setTimeout(() => { typing.hidden = true; }, TYPING_VISIBLE_MS);
        scrollToEnd();
    }

    // --- Сообщения -----------------------------------------------------------

    // Теги сообщения ACS приходят то массивом, то строкой через запятую
    // («system,agentendconversation,displaytocustomer»), в зависимости от версии канала.
    function hasTag(message: SdkMessage, tag: string): boolean {
        const sources = [message.tags, message.metadata?.tags, message.properties?.tags];
        return sources.some((source) => {
            const list = Array.isArray(source) ? source : (source ?? "").toString().replace(/"/g, "").split(",");
            return list.some((item) => item.trim().toLowerCase() === tag);
        });
    }

    function classify(message: SdkMessage): Bubble["kind"] {
        const role = (message.role ?? "").toLowerCase();
        if (role === "system" || hasTag(message, "system")) {
            return "system";
        }
        if (role === "user" || message.sender?.displayName === CUSTOMER_DISPLAY_NAME) {
            return "user";
        }
        return "agent";
    }

    function handleMessage(message: SdkMessage): void {
        // Служебные события (набор текста, состояние) приходят без содержимого.
        const hasFile = !!message.fileMetadata?.name;
        if (!hasFile && !(message.content ?? "").trim()) {
            return;
        }

        const key = message.id ?? message.messageid ?? message.clientmessageid;
        if (key) {
            if (seen.has(key)) {
                return;
            }
            seen.add(key);
        }

        const kind = classify(message);
        const agentEnded = hasTag(message, "agentendconversation");
        if (kind === "system" && !agentEnded) {
            void checkClosed();
        }

        if (kind === "user") {
            // Свои сообщения уже показаны при отправке; из SDK приходит эхо
            // и, после переподключения, история.
            const echo = pendingEcho.indexOf(message.content ?? "");
            if (echo >= 0) {
                pendingEcho.splice(echo, 1);
                return;
            }
        }

        typing.hidden = kind !== "agent" ? typing.hidden : true;

        const bubble: Bubble = {
            kind,
            sender: agentName(message.sender?.displayName),
            time: message.timestamp
        };

        if (hasFile) {
            bubble.file = {
                name: message.fileMetadata!.name!,
                size: message.fileMetadata!.size,
                meta: kind === "user" ? undefined : message.fileMetadata
            };
        } else if (message.contentType === "RichText" && /<[a-z][^>]*>/i.test(message.content ?? "")) {
            bubble.html = message.content;
        } else if (kind === "agent") {
            bubble.markdown = message.content;
        } else {
            bubble.text = message.content;
        }

        addBubble(bubble);

        // Агент закрыл разговор: Dynamics помечает это сообщение тегом
        // agentendconversation — завершаем чат сразу, не дожидаясь опроса состояния.
        if (agentEnded) {
            finish();
            return;
        }

        if (kind === "agent") {
            setStatus("");
            if (!isOpen()) {
                root!.classList.add("has-unread");
            }
        }
    }

    // --- Рабочие часы --------------------------------------------------------

    async function init(): Promise<ChatSdk> {
        sdk = await loadSdk();
        if (!initialized) {
            // initialize() заодно отдаёт настройки виджета, нужные для проверки часов.
            lastConfig = (await sdk.initialize()) ?? null;
            initialized = true;
        }
        return sdk;
    }

    let lastConfig: Awaited<ReturnType<ChatSdk["initialize"]>> | null = null;

    function formatHours(start?: string, end?: string): string {
        const from = start ? new Date(start) : null;
        const to = end ? new Date(end) : null;
        if (!from || !to || Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
            return "";
        }

        const lang = document.documentElement.lang || undefined;
        const time: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit" };
        const day = from.toLocaleDateString(lang, { weekday: "long" });
        return `${day}, ${from.toLocaleTimeString(lang, time)} – ${to.toLocaleTimeString(lang, time)}`;
    }

    // Узнаём, работает ли поддержка, до того как посетитель нажмёт «Начать чат»:
    // вне рабочих часов Live Chat Widget не принимает обращения, и вместо кнопки
    // показываем время работы и ссылку на создание заявки.
    async function probe(): Promise<void> {
        if (offline !== null || probing || phase !== "idle") {
            return;
        }

        probing = true;
        startButton.disabled = true;
        setStatus(t("connecting"));

        try {
            const chat = await init();
            const info = await chat.getAgentAvailability();
            offline = lastConfig?.LiveWSAndLiveChatEngJoin?.OutOfOperatingHours === "True"
                || info?.isQueueAvailable === false;

            if (offline) {
                const hours = formatHours(info?.StartTimeOfNextOperatingHour, info?.EndTimeOfNextOperatingHour);
                welcomeText.textContent = hours ? t("offlineHours").replace("{0}", hours) : t("offline");
                startButton.hidden = true;
                requestLink.hidden = false;
            }
        } catch (error) {
            // Не смогли проверить — пусть попробует начать чат: ошибку покажет connect().
            console.warn("Chat availability check failed", error);
            offline = false;
        } finally {
            probing = false;
            startButton.disabled = false;
            setStatus("");
        }
    }

    // --- Разговор ------------------------------------------------------------

    async function connect(savedContext?: Record<string, unknown>): Promise<void> {
        setPhase("connecting");
        showNotice("");

        try {
            await init();

            // Язык разговора SDK берёт из настроек виджета (en-us и т.п.). Свой locale
            // вроде «en» сервер Omnichannel отвергает: ConversationInitializationFailure.
            const params: Record<string, unknown> = {};
            if (savedContext) {
                params.liveChatContext = savedContext;
            }
            await sdk!.startChat(params);

            const context = await sdk!.getCurrentLiveChatContext();
            writeSaved({ context, open: isOpen() });

            await sdk!.onNewMessage(handleMessage);
            await sdk!.onTypingEvent(showTyping);
            await sdk!.onAgentEndSession(() => finish());

            if (savedContext) {
                const history = (await sdk!.getMessages()) ?? [];
                history
                    .slice()
                    .sort((a, b) => new Date(a.timestamp ?? 0).getTime() - new Date(b.timestamp ?? 0).getTime())
                    .forEach(handleMessage);
            }

            setPhase("active");
            setStatus("");
            if (isOpen()) {
                input.focus();
            }
        } catch (error) {
            const details = error as { message?: string; httpResponseStatusCode?: number; exceptionDetails?: unknown };
            console.warn("Chat connection failed", details?.message, details?.httpResponseStatusCode, details?.exceptionDetails);
            writeSaved(null);
            sdk = null;
            initialized = false;
            sdkLoading = null;
            setPhase("error");
            showNotice(t("error"));
        }
    }

    let closeChecking = false;
    /** Сколько ещё раз спросить состояние; каждое новое событие снова даёт полный запас. */
    let closeBudget = 0;

    // Агент закрыл разговор: SDK сообщает об этом не всегда (onAgentEndSession срабатывает
    // по обновлению треда и с задержкой), зато в ленте приходит системное сообщение.
    // На любое системное сообщение и на неудачную отправку спрашиваем состояние
    // разговора; закрыт (WrapUp/Closed) или пропал — завершаем чат в интерфейсе.
    async function checkClosed(): Promise<void> {
        closeBudget = 4;
        if (closeChecking) {
            return;
        }

        closeChecking = true;
        try {
            while (closeBudget > 0 && phase === "active" && sdk) {
                closeBudget--;
                try {
                    const details = await sdk.getConversationDetails();
                    if (!details || Object.keys(details).length === 0 || details.state === "WrapUp" || details.state === "Closed") {
                        finish();
                        return;
                    }
                } catch {
                    /* временная ошибка сети — повторим */
                }
                await new Promise((resolve) => window.setTimeout(resolve, 2000));
            }
        } finally {
            closeChecking = false;
        }
    }

    function finish(): void {
        if (phase === "ended") {
            return;
        }
        writeSaved(null);
        typing.hidden = true;
        setPhase("ended");
        showNotice(t("ended"), [{ label: t("newChat"), run: reset, primary: true }]);
    }

    function reset(): void {
        log.replaceChildren();
        seen.clear();
        pendingEcho.length = 0;
        lastSender = null;
        sdk = null;
        sdkLoading = null;
        initialized = false;
        offline = null;
        showNotice("");
        setPhase("idle");
        startButton.focus();
    }

    async function endChat(): Promise<void> {
        showNotice("");
        try {
            await sdk?.endChat();
        } catch (error) {
            console.warn("Chat end failed", error);
        }
        writeSaved(null);
        reset();
        setOpen(false);
    }

    function askToEnd(): void {
        showNotice(t("endConfirm"), [
            { label: t("endChat"), run: () => void endChat(), primary: true },
            { label: t("keep"), run: () => showNotice("") }
        ]);
    }

    async function send(): Promise<void> {
        const text = input.value.trim();
        if (!text || phase !== "active" || !sdk) {
            return;
        }

        input.value = "";
        autosize();
        pendingEcho.push(text);
        addBubble({ kind: "user", text });
        showNotice("");

        try {
            await sdk.sendMessage({ content: text });
        } catch (error) {
            console.warn("Chat send failed", error);
            const echo = pendingEcho.indexOf(text);
            if (echo >= 0) {
                pendingEcho.splice(echo, 1);
            }
            showNotice(t("sendFailed"));
            void checkClosed();
        }
    }

    async function upload(file: File): Promise<void> {
        if (!sdk || phase !== "active") {
            return;
        }
        if (file.size > MAX_FILE_BYTES) {
            showNotice(t("fileTooLarge"));
            return;
        }

        showNotice("");
        addBubble({ kind: "user", file: { name: file.name, size: file.size } });
        try {
            await sdk.uploadFileAttachment(file);
        } catch (error) {
            console.warn("Chat upload failed", error);
            showNotice(t("sendFailed"));
            void checkClosed();
        }
    }

    function autosize(): void {
        // box-sizing: border-box, поэтому к высоте содержимого прибавляем рамку.
        input.style.height = "auto";
        const height = input.scrollHeight + (input.offsetHeight - input.clientHeight);
        input.style.height = `${Math.min(height, 120)}px`;
        input.style.overflowY = height > 120 ? "auto" : "hidden";
    }

    // --- События -------------------------------------------------------------

    launcher.addEventListener("click", () => {
        root!.classList.remove("has-unread");
        setOpen(!isOpen());
    });

    minimizeButton.addEventListener("click", () => setOpen(false));
    startButton.addEventListener("click", () => void connect());
    endButton.addEventListener("click", askToEnd);

    form.addEventListener("submit", (event) => {
        event.preventDefault();
        void send();
    });

    input.addEventListener("keydown", (event) => {
        // Enter отправляет, Shift+Enter — перенос строки; во время ввода
        // через IME (isComposing) Enter подтверждает набор, а не отправляет.
        if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
            event.preventDefault();
            void send();
        }
    });

    input.addEventListener("input", () => {
        autosize();
        const now = Date.now();
        if (sdk && phase === "active" && input.value && now - lastTypingSent > TYPING_SEND_INTERVAL_MS) {
            lastTypingSent = now;
            sdk.sendTypingEvent().catch(() => { /* индикатор не критичен */ });
        }
    });

    attachButton.addEventListener("click", () => fileInput.click());
    fileInput.addEventListener("change", () => {
        const file = fileInput.files?.[0];
        fileInput.value = "";
        if (file) {
            void upload(file);
        }
    });

    panel.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
            event.stopPropagation();
            setOpen(false);
        }
    });

    // --- Старт ---------------------------------------------------------------

    setPhase("idle");

    const saved = readSaved();
    if (saved?.context) {
        if (saved.open) {
            setOpen(true, false);
        }
        void connect(saved.context);
    }
})();
